import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { adminApi, eur, fmtDate, expiresText, STATUS, PLAN, METHOD } from "@/lib/adminApi";
import { DevicePayments } from "@/components/admin/DevicePayments";
import { DeviceHistory } from "@/components/admin/DeviceHistory";

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.expired;
  return <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${s.cls}`} data-testid="device-status-badge">{s.label}</span>;
};

const Row = ({ k, v }) => (
  <div><dt className="text-[10px] font-bold uppercase tracking-widest text-muted2">{k}</dt><dd className="mt-0.5 text-sm">{v}</dd></div>
);

const ActBtn = ({ children, danger, ...p }) => (
  <button {...p} className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${danger ? "border-red-500/40 text-red-400 hover:bg-red-500/10" : "border-lineStrong text-white hover:bg-surface3"}`}>{children}</button>
);

export const DeviceActions = ({ d, askConfirm, runAction }) => {
  const [days, setDays] = useState(7);
  const id = { device_id: d.device_id };
  const post = (path, body, msg) => runAction(() => adminApi.post(path, { ...id, ...body }), msg);
  const k = d.device_key;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`device-actions-${k}`}>
      <ActBtn onClick={() => post("/admin/devices/activate", { plan: "yearly" }, "Uređaj aktiviran (godišnje).")} data-testid={`act-yearly-${k}`}>Godišnje 9.99€</ActBtn>
      <ActBtn onClick={() => post("/admin/devices/activate", { plan: "lifetime" }, "Uređaj aktiviran (doživotno).")} data-testid={`act-lifetime-${k}`}>Doživotno 29.99€</ActBtn>
      <span className="flex items-center gap-1 rounded-lg border border-lineStrong pl-2">
        <input type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} className="w-12 bg-transparent text-xs outline-none" data-testid={`trial-days-${k}`} />
        <button onClick={() => post("/admin/devices/grant-trial", { days: Number(days) || 7 }, `Proba ${days} dana dodeljena.`)} className="rounded-r-lg px-3 py-2 text-xs font-semibold hover:bg-surface3" data-testid={`act-trial-${k}`}>Proba {days} dana</button>
      </span>
      {d.activated && (
        <ActBtn danger data-testid={`act-deactivate-${k}`} onClick={() => askConfirm({ title: `Deaktiviraj ${k}?`, text: "Uređaj gubi plaćenu aktivaciju i vraća se na probni status.", ok: "Deaktiviraj", onOk: () => post("/admin/devices/deactivate", {}, "Uređaj deaktiviran.") })}>Deaktiviraj</ActBtn>
      )}
      {d.blocked ? (
        <ActBtn onClick={() => post("/admin/devices/unblock", {}, "Uređaj odblokiran.")} data-testid={`act-unblock-${k}`}>Odblokiraj</ActBtn>
      ) : (
        <ActBtn danger data-testid={`act-block-${k}`} onClick={() => askConfirm({ title: `Blokiraj ${k}?`, text: "Blokirani uređaj ne može da pušta sadržaj dok ga ne odblokirate.", ok: "Blokiraj", onOk: () => post("/admin/devices/block", {}, "Uređaj blokiran.") })}>Blokiraj</ActBtn>
      )}
    </div>
  );
};

export const DeviceCard = ({ d, askConfirm, runAction }) => {
  const [open, setOpen] = useState(null);
  const payment = d.paid ? `${eur(d.paid_amount)} • ${d.email || "bez emaila"} • ${METHOD[d.payment_method]}` : METHOD[d.payment_method] || "—";
  return (
    <div className="rounded-2xl border border-line bg-surface2 p-5" data-testid={`device-card-${d.device_key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-2xl font-bold tracking-[3px]" data-testid="device-card-key">{d.device_key}</p>
          <p className="font-mono text-xs text-txt2">{d.mac}</p>
        </div>
        <StatusBadge status={d.status} />
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Row k="Plan" v={PLAN[d.plan] || d.plan || "—"} />
        <Row k="Ističe" v={expiresText(d)} />
        <Row k="Uplata" v={payment} />
        {d.paid && <Row k="Telefon" v={d.phone ? <a href={`tel:${d.phone}`} className="text-brand" data-testid="device-card-phone">{d.phone}</a> : "—"} />}
        <Row k="Kreiran" v={fmtDate(d.created_at)} />
        <Row k="Poslednja aktivnost" v={fmtDate(d.last_seen)} />
        {d.paid_at && <Row k="Plaćeno" v={fmtDate(d.paid_at)} />}
      </dl>
      <div className="mt-4"><DeviceActions d={d} askConfirm={askConfirm} runAction={runAction} /></div>
      <div className="mt-4 flex gap-4">
        {[["payments", "Uplate"], ["history", "Istorija"]].map(([id, label]) => (
          <button key={id} onClick={() => setOpen(open === id ? null : id)} className={`flex items-center gap-1 text-xs font-semibold hover:text-white ${open === id ? "text-white" : "text-txt2"}`} data-testid={`toggle-${id}-${d.device_key}`}>
            {label} <CaretDown size={14} className={`transition-transform ${open === id ? "rotate-180" : ""}`} />
          </button>
        ))}
      </div>
      {open === "payments" && <div className="mt-2"><DevicePayments deviceId={d.device_id} askConfirm={askConfirm} runAction={runAction} /></div>}
      {open === "history" && <div className="mt-3"><DeviceHistory d={d} /></div>}
    </div>
  );
};
